/**
 * @license
 * Copyright (c) 2026 STACK SOFTWARE. All Rights Reserved.
 * Proprietary and confidential. See LICENSE in the project root.
 */
import { DOCUMENT, Location } from '@angular/common';
import {
  DestroyRef,
  Injectable,
  InjectionToken,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, NavigationError, Router } from '@angular/router';

/**
 * The slice of `window` this service touches, behind a token so tests can
 * observe the hard reload without jsdom actually navigating.
 */
export type ChunkRecoveryWindow = Pick<Window, 'location' | 'sessionStorage'>;

export const CHUNK_RECOVERY_WINDOW = new InjectionToken<ChunkRecoveryWindow | null>(
  'CHUNK_RECOVERY_WINDOW',
  { providedIn: 'root', factory: () => inject(DOCUMENT).defaultView },
);

/** sessionStorage key holding the URL we last hard-reloaded to recover. */
export const CHUNK_RELOAD_KEY = 'stack:chunk-reload-url';

/**
 * Per-browser wording of "a lazy chunk could not be fetched". The esbuild
 * application builder emits native ESM `import()`, so the first three are what
 * production sees; the last two cover webpack-style chunk loading.
 */
const CHUNK_ERROR_PATTERNS = [
  /Failed to fetch dynamically imported module/i, // Chrome, Edge
  /error loading dynamically imported module/i, // Firefox
  /Importing a module script failed/i, // Safari
  /Loading chunk [\w-]+ failed/i, // webpack
  /ChunkLoadError/,
];

export function isChunkLoadError(error: unknown): boolean {
  if (!error) {
    return false;
  }
  const err = error as { name?: unknown; message?: unknown };
  const text = [err.name, err.message, typeof error === 'string' ? error : '']
    .filter((part): part is string => typeof part === 'string')
    .join(' ');
  return CHUNK_ERROR_PATTERNS.some((pattern) => pattern.test(text));
}

/**
 * Recovers open tabs from a deploy.
 *
 * Every deploy ships a new nginx image containing only the new build's hashed
 * chunks. A tab still running the previous build asks for a chunk that no
 * longer exists, the 404 rejects the lazy `loadChildren` import, and the
 * router cancels the navigation — so menu clicks silently do nothing.
 *
 * On that specific failure we do a full page load of the URL the user was
 * heading to. index.html is served no-store (deployment/stack-web/nginx.conf),
 * so the reload boots the current build and lands where they clicked.
 *
 * Loop guard: the target URL is remembered in sessionStorage and we reload at
 * most once per URL until a navigation succeeds. If the chunk is genuinely
 * missing on the new build too, the user sees the normal failure instead of a
 * reload loop.
 */
@Injectable({ providedIn: 'root' })
export class ChunkLoadRecoveryService {
  private readonly router = inject(Router);
  private readonly location = inject(Location);
  private readonly win = inject(CHUNK_RECOVERY_WINDOW);
  private readonly destroyRef = inject(DestroyRef);
  private started = false;

  start(): void {
    const win = this.win;
    if (this.started || !win) {
      return;
    }
    this.started = true;

    this.router.events
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        if (event instanceof NavigationEnd) {
          // A successful navigation re-arms recovery for the next deploy.
          this.clearGuard(win);
        } else if (
          event instanceof NavigationError &&
          isChunkLoadError(event.error)
        ) {
          this.recover(win, event.url);
        }
      });
  }

  private recover(win: ChunkRecoveryWindow, url: string): void {
    let lastAttempt: string | null;
    try {
      lastAttempt = win.sessionStorage.getItem(CHUNK_RELOAD_KEY);
    } catch {
      // No storage means no loop guard; a reload loop is worse than the
      // original dead click, so leave the failure as-is.
      return;
    }
    if (lastAttempt === url) {
      console.error(
        `Chunk load failed again for ${url} after reloading; not retrying.`,
      );
      return;
    }

    try {
      win.sessionStorage.setItem(CHUNK_RELOAD_KEY, url);
    } catch {
      return;
    }
    console.warn(
      `Lazy chunk failed to load for ${url} (likely a new deploy); reloading.`,
    );
    win.location.assign(this.location.prepareExternalUrl(url));
  }

  private clearGuard(win: ChunkRecoveryWindow): void {
    try {
      win.sessionStorage.removeItem(CHUNK_RELOAD_KEY);
    } catch {
      // Storage unavailable: nothing to clear.
    }
  }
}
